import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import axios from '../axiosConfig';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import i18n from '../i18n';

const WS_URL = process.env.REACT_APP_NOTIFICATIONS_WSS_URL || "http://localhost:8080/ws";

export const ChatContext = createContext();

export const ChatProvider = ({ children, userId }) => {
    const [chatRooms, setChatRooms] = useState([]);
    const [archivedChatRooms, setArchivedChatRooms] = useState([]);
    const [activeChatRoom, setActiveChatRoom] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalUnreadCount, setTotalUnreadCount] = useState(0);
    const [typingUsers, setTypingUsers] = useState({});
    const [onlineUsers, setOnlineUsers] = useState({});
    const stompClientRef = useRef(null);
    const subscriptionsRef = useRef({});
    const activeChatRoomRef = useRef(null);
    const lastTypingSentRef = useRef(0);
    const typingTimeoutsRef = useRef({});
    const chatRoomsRef = useRef([]);
    const heartbeatIntervalRef = useRef(null);

    // Request notification permission on first mount
    useEffect(() => {
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    const requestNotificationPermission = useCallback(() => {
        if (typeof Notification !== 'undefined') {
            Notification.requestPermission();
        }
    }, []);

    // Keep chatRoomsRef in sync for use in callbacks without stale closures
    useEffect(() => {
        chatRoomsRef.current = chatRooms;
    }, [chatRooms]);

    // Fetch all chat rooms for the current user
    const fetchChatRooms = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/v1/chat/rooms');
            setChatRooms(response.data);

            const total = response.data.reduce((sum, room) => sum + (room.unreadCount || 0), 0);
            setTotalUnreadCount(total);
        } catch (err) {
            console.error('Failed to fetch chat rooms:', err);
            setError(i18n.t('chat:errors.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch archived chat rooms
    const fetchArchivedChatRooms = useCallback(async () => {
        try {
            const response = await axios.get('/api/v1/chat/rooms/archived');
            setArchivedChatRooms(response.data);
        } catch (err) {
            console.error('Failed to fetch archived chat rooms:', err);
        }
    }, []);

    // Fetch a single chat room details
    const fetchChatRoom = useCallback(async (chatRoomId) => {
        try {
            const response = await axios.get(`/api/v1/chat/rooms/${chatRoomId}`);
            setActiveChatRoom(response.data);
            return response.data;
        } catch (err) {
            console.error('Failed to fetch chat room:', err);
            setError(i18n.t('chat:errors.loadRoomFailed'));
            return null;
        }
    }, []);

    // Fetch messages for a chat room with pagination
    const fetchMessages = useCallback(async (chatRoomId, page = 0, size = 50) => {
        try {
            setMessagesLoading(true);
            const response = await axios.get(`/api/v1/chat/messages/room/${chatRoomId}`, {
                params: { page, size }
            });
            if (page === 0) {
                setMessages(response.data.content.reverse());
            } else {
                setMessages(prev => [...response.data.content.reverse(), ...prev]);
            }
            return response.data;
        } catch (err) {
            console.error('Failed to fetch messages:', err);
            setError(i18n.t('chat:errors.loadMessagesFailed'));
            return null;
        } finally {
            setMessagesLoading(false);
        }
    }, []);

    // Send a message
    const sendMessage = useCallback(async (chatRoomId, content, parentMessageId) => {
        try {
            const payload = { chatRoomId, content };
            if (parentMessageId) {
                payload.parentMessageId = parentMessageId;
            }
            const response = await axios.post('/api/v1/chat/messages', payload);
            return response.data;
        } catch (err) {
            console.error('Failed to send message:', err);
            if (err.response?.data?.message) {
                throw new Error(err.response.data.message);
            }
            throw new Error(i18n.t('chat:errors.sendFailed'));
        }
    }, []);

    // Edit a message
    const editMessage = useCallback(async (messageId, content) => {
        try {
            const response = await axios.put(`/api/v1/chat/messages/${messageId}`, {
                content
            });
            setMessages(prev => prev.map(msg =>
                msg.id === messageId ? response.data : msg
            ));
            return response.data;
        } catch (err) {
            console.error('Failed to edit message:', err);
            throw new Error(i18n.t('chat:errors.editFailed'));
        }
    }, []);

    // Delete a message
    const deleteMessage = useCallback(async (messageId) => {
        try {
            const response = await axios.delete(`/api/v1/chat/messages/${messageId}`);
            setMessages(prev => prev.map(msg =>
                msg.id === messageId ? response.data : msg
            ));
            return response.data;
        } catch (err) {
            console.error('Failed to delete message:', err);
            throw new Error(i18n.t('chat:errors.deleteFailed'));
        }
    }, []);

    // Mark chat as read
    const markAsRead = useCallback(async (chatRoomId) => {
        try {
            await axios.patch(`/api/v1/chat/rooms/${chatRoomId}/read`);
            setChatRooms(prev => {
                const room = prev.find(r => r.id === chatRoomId);
                if (room?.unreadCount > 0) {
                    setTotalUnreadCount(total => Math.max(0, total - room.unreadCount));
                }
                return prev.map(r =>
                    r.id === chatRoomId ? { ...r, unreadCount: 0 } : r
                );
            });
        } catch (err) {
            console.error('Failed to mark as read:', err);
        }
    }, []);

    // Send typing event via REST (debounced - only once per 3 seconds)
    const sendTypingEvent = useCallback((chatRoomId) => {
        const now = Date.now();
        if (now - lastTypingSentRef.current < 3000) return;
        lastTypingSentRef.current = now;

        axios.post(`/api/v1/chat/rooms/${chatRoomId}/typing`).catch(() => {
            // Typing indicator is best-effort, ignore errors
        });
    }, []);

    // Toggle mute for a chat room
    const toggleMute = useCallback(async (chatRoomId) => {
        try {
            const response = await axios.patch(`/api/v1/chat/rooms/${chatRoomId}/mute`);
            setChatRooms(prev => prev.map(room =>
                room.id === chatRoomId ? { ...room, muted: response.data?.muted ?? !room.muted } : room
            ));
            if (activeChatRoomRef.current?.id === chatRoomId) {
                setActiveChatRoom(prev => prev ? { ...prev, muted: response.data?.muted ?? !prev.muted } : prev);
            }
            return response.data;
        } catch (err) {
            console.error('Failed to toggle mute:', err);
            throw new Error(i18n.t('chat:errors.muteFailed'));
        }
    }, []);

    // Pin a message
    const pinMessage = useCallback(async (messageId) => {
        try {
            const response = await axios.patch(`/api/v1/chat/messages/${messageId}/pin`);
            setMessages(prev => prev.map(msg =>
                msg.id === messageId ? { ...msg, pinned: true } : msg
            ));
            return response.data;
        } catch (err) {
            console.error('Failed to pin message:', err);
            throw new Error(i18n.t('chat:errors.pinFailed'));
        }
    }, []);

    // Unpin a message
    const unpinMessage = useCallback(async (messageId) => {
        try {
            const response = await axios.patch(`/api/v1/chat/messages/${messageId}/unpin`);
            setMessages(prev => prev.map(msg =>
                msg.id === messageId ? { ...msg, pinned: false } : msg
            ));
            return response.data;
        } catch (err) {
            console.error('Failed to unpin message:', err);
            throw new Error(i18n.t('chat:errors.unpinFailed'));
        }
    }, []);

    // Fetch pinned messages for a chat room
    const fetchPinnedMessages = useCallback(async (chatRoomId) => {
        try {
            const response = await axios.get(`/api/v1/chat/messages/room/${chatRoomId}/pinned`);
            return response.data;
        } catch (err) {
            console.error('Failed to fetch pinned messages:', err);
            return [];
        }
    }, []);

    // Add reaction to a message
    const addReaction = useCallback(async (messageId, emoji) => {
        try {
            const response = await axios.post(`/api/v1/chat/messages/${messageId}/reactions`, { emoji });
            setMessages(prev => prev.map(msg => {
                if (msg.id === messageId) {
                    const reactions = msg.reactions ? [...msg.reactions] : [];
                    reactions.push(response.data);
                    return { ...msg, reactions };
                }
                return msg;
            }));
            return response.data;
        } catch (err) {
            console.error('Failed to add reaction:', err);
            throw new Error(i18n.t('chat:errors.reactionAddFailed'));
        }
    }, []);

    // Remove reaction from a message
    const removeReaction = useCallback(async (messageId, emoji) => {
        try {
            await axios.delete(`/api/v1/chat/messages/${messageId}/reactions/${emoji}`);
            setMessages(prev => prev.map(msg => {
                if (msg.id === messageId) {
                    const reactions = (msg.reactions || []).filter(
                        r => !(r.emoji === emoji && r.userId === userId)
                    );
                    return { ...msg, reactions };
                }
                return msg;
            }));
        } catch (err) {
            console.error('Failed to remove reaction:', err);
            throw new Error(i18n.t('chat:errors.reactionRemoveFailed'));
        }
    }, [userId]);

    // Fetch presence for a chat room
    const fetchPresence = useCallback(async (chatRoomId) => {
        try {
            const response = await axios.get(`/api/v1/chat/rooms/${chatRoomId}/presence`);
            setOnlineUsers(response.data);
            return response.data;
        } catch (err) {
            console.error('Failed to fetch presence:', err);
            return {};
        }
    }, []);

    // Create direct chat
    const createDirectChat = useCallback(async (targetUserId) => {
        try {
            const response = await axios.post('/api/v1/chat/rooms/direct', {
                targetUserId
            });
            await fetchChatRooms();
            return response.data;
        } catch (err) {
            console.error('Failed to create direct chat:', err);
            throw new Error(i18n.t('chat:errors.createFailed'));
        }
    }, [fetchChatRooms]);

    // Create training group chat
    const createTrainingGroupChat = useCallback(async (trainingGroupId) => {
        try {
            const response = await axios.post('/api/v1/chat/rooms/training-group', {
                trainingGroupId
            });
            await fetchChatRooms();
            return response.data;
        } catch (err) {
            console.error('Failed to create training group chat:', err);
            if (err.response?.data?.message) {
                throw new Error(err.response.data.message);
            }
            throw new Error(i18n.t('chat:errors.createGroupChatFailed'));
        }
    }, [fetchChatRooms]);

    // Create school announcement chat
    const createSchoolAnnouncementChat = useCallback(async (schoolId) => {
        try {
            const response = await axios.post(`/api/v1/chat/rooms/school-announcement/${schoolId}`);
            await fetchChatRooms();
            return response.data;
        } catch (err) {
            console.error('Failed to create announcement chat:', err);
            throw new Error(i18n.t('chat:errors.createAnnouncementFailed'));
        }
    }, [fetchChatRooms]);

    // Add all school owners to announcement chat
    const addAllSchoolOwners = useCallback(async (chatRoomId) => {
        try {
            await axios.post(`/api/v1/chat/rooms/${chatRoomId}/add-all-school-owners`);
            await fetchChatRoom(chatRoomId);
        } catch (err) {
            console.error('Failed to add school owners:', err);
            throw new Error(i18n.t('chat:errors.addOwnersFailed'));
        }
    }, [fetchChatRoom]);

    // Create custom group chat
    const createCustomGroupChat = useCallback(async (name, participantIds) => {
        try {
            const response = await axios.post('/api/v1/chat/rooms/custom-group', {
                name,
                participantIds
            });
            await fetchChatRooms();
            return response.data;
        } catch (err) {
            console.error('Failed to create custom group chat:', err);
            throw new Error(i18n.t('chat:errors.createFailed'));
        }
    }, [fetchChatRooms]);

    // Create trainer chat
    const createTrainerChat = useCallback(async (trainerIds, name) => {
        try {
            const response = await axios.post('/api/v1/chat/rooms/trainer', {
                trainerIds,
                name
            });
            await fetchChatRooms();
            return response.data;
        } catch (err) {
            console.error('Failed to create trainer chat:', err);
            throw new Error(i18n.t('chat:errors.createTrainerChatFailed'));
        }
    }, [fetchChatRooms]);

    // Finish training group chat
    const finishTrainingGroupChat = useCallback(async (chatRoomId) => {
        try {
            const response = await axios.patch(`/api/v1/chat/rooms/${chatRoomId}/finish`);
            await fetchChatRooms();
            return response.data;
        } catch (err) {
            console.error('Failed to finish chat:', err);
            throw new Error(i18n.t('chat:errors.finishFailed'));
        }
    }, [fetchChatRooms]);

    // Renew training group chat
    const renewTrainingGroupChat = useCallback(async (chatRoomId) => {
        try {
            const response = await axios.patch(`/api/v1/chat/rooms/${chatRoomId}/renew`);
            await fetchChatRooms();
            return response.data;
        } catch (err) {
            console.error('Failed to renew chat:', err);
            throw new Error(i18n.t('chat:errors.renewFailed'));
        }
    }, [fetchChatRooms]);

    // Delete chat
    const deleteChat = useCallback(async (chatRoomId) => {
        try {
            await axios.delete(`/api/v1/chat/rooms/${chatRoomId}`);
            setActiveChatRoom(null);
            setMessages([]);
            await fetchChatRooms();
        } catch (err) {
            console.error('Failed to delete chat:', err);
            throw new Error(i18n.t('chat:errors.deleteChatFailed'));
        }
    }, [fetchChatRooms]);

    // Add participants
    const addParticipants = useCallback(async (chatRoomId, userIds) => {
        try {
            await axios.post(`/api/v1/chat/rooms/${chatRoomId}/participants`, {
                userIds
            });
            await fetchChatRoom(chatRoomId);
        } catch (err) {
            console.error('Failed to add participants:', err);
            throw new Error(i18n.t('chat:errors.participantsAddFailed'));
        }
    }, [fetchChatRoom]);

    // Remove participant
    const removeParticipant = useCallback(async (chatRoomId, participantUserId) => {
        try {
            await axios.delete(`/api/v1/chat/rooms/${chatRoomId}/participants/${participantUserId}`);
            await fetchChatRoom(chatRoomId);
        } catch (err) {
            console.error('Failed to remove participant:', err);
            throw new Error(i18n.t('chat:errors.participantRemoveFailed'));
        }
    }, [fetchChatRoom]);

    // WebSocket connection
    useEffect(() => {
        if (!userId) return;

        const token = localStorage.getItem('jwt');
        const client = new Client({
            webSocketFactory: () => new SockJS(WS_URL),
            reconnectDelay: 5000,
            connectHeaders: token ? { Authorization: `Bearer ${token}` } : {},
        });

        client.onConnect = () => {
            stompClientRef.current = client;

            // Subscribe to user-specific chat events
            client.subscribe(`/topic/chat/user/${userId}`, (message) => {
                if (message.body) {
                    const event = JSON.parse(message.body);
                    handleChatEvent(event);
                }
            });
        };

        client.onStompError = (frame) => {
            console.error('Chat STOMP error:', frame.headers['message']);
        };

        client.activate();

        return () => {
            Object.values(subscriptionsRef.current).forEach(sub => {
                try { sub.unsubscribe(); } catch (e) { /* ignore */ }
            });
            subscriptionsRef.current = {};
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
            }
            client.deactivate();
        };
    }, [userId]);

    // Subscribe to a specific chat room's messages
    const subscribeToChatRoom = useCallback((chatRoomId) => {
        const client = stompClientRef.current;
        if (!client || !client.connected) return;

        // Unsubscribe from previous room
        if (subscriptionsRef.current.messages) {
            try { subscriptionsRef.current.messages.unsubscribe(); } catch (e) { /* ignore */ }
        }
        if (subscriptionsRef.current.events) {
            try { subscriptionsRef.current.events.unsubscribe(); } catch (e) { /* ignore */ }
        }

        // Subscribe to new room messages
        subscriptionsRef.current.messages = client.subscribe(
            `/topic/chat/${chatRoomId}`,
            (message) => {
                if (message.body) {
                    const newMessage = JSON.parse(message.body);
                    setMessages(prev => {
                        if (prev.some(m => m.id === newMessage.id)) return prev;
                        return [...prev, newMessage];
                    });
                }
            }
        );

        // Subscribe to chat events (edit, delete, etc.)
        subscriptionsRef.current.events = client.subscribe(
            `/topic/chat/${chatRoomId}/events`,
            (message) => {
                if (message.body) {
                    const event = JSON.parse(message.body);
                    handleRoomEvent(event);
                }
            }
        );
    }, []);

    // Keep activeChatRoomRef in sync
    useEffect(() => {
        activeChatRoomRef.current = activeChatRoom;
    }, [activeChatRoom]);

    const handleChatEvent = useCallback((event) => {
        switch (event.type) {
            case 'NEW_CHAT':
                fetchChatRooms();
                break;
            case 'CHAT_DELETED':
                setChatRooms(prev => prev.filter(r => r.id !== event.payload?.chatRoomId));
                break;
            case 'NEW_MESSAGE': {
                const { chatRoomId, message } = event.payload || {};
                if (!chatRoomId) break;

                // If user is currently viewing this chat, don't increment unread
                // (markAsRead will be called by the ChatRoom component)
                const isViewingThisChat = activeChatRoomRef.current?.id === chatRoomId;

                setChatRooms(prev => {
                    const updated = prev.map(room => {
                        if (room.id === chatRoomId) {
                            return {
                                ...room,
                                lastMessage: message,
                                updatedAt: message?.createdAt || room.updatedAt,
                                unreadCount: isViewingThisChat
                                    ? room.unreadCount
                                    : (room.unreadCount || 0) + 1
                            };
                        }
                        return room;
                    });
                    // Sort by most recent activity
                    return updated.sort((a, b) =>
                        new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
                    );
                });

                if (!isViewingThisChat) {
                    setTotalUnreadCount(prev => prev + 1);
                }

                // Browser notification when tab is hidden and room is not muted
                if (document.hidden && message) {
                    const room = chatRoomsRef.current.find(r => r.id === chatRoomId);
                    if (!room?.muted && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                        const senderName = message.senderFirstName || i18n.t('chat:newMessage');
                        const content = message.content?.length > 50
                            ? message.content.substring(0, 50) + '...'
                            : message.content || '';
                        new Notification(senderName, { body: content });
                    }
                }
                break;
            }
            case 'TYPING': {
                const { chatRoomId: typingRoomId, userId: typingUserId, firstName } = event.payload || {};
                if (!typingRoomId || typingUserId === userId) break;

                // Clear previous timeout for this room
                if (typingTimeoutsRef.current[typingRoomId]) {
                    clearTimeout(typingTimeoutsRef.current[typingRoomId]);
                }

                setTypingUsers(prev => ({
                    ...prev,
                    [typingRoomId]: { userId: typingUserId, firstName }
                }));

                // Clear typing after 3 seconds
                typingTimeoutsRef.current[typingRoomId] = setTimeout(() => {
                    setTypingUsers(prev => {
                        const next = { ...prev };
                        delete next[typingRoomId];
                        return next;
                    });
                }, 3000);
                break;
            }
            case 'MESSAGE_READ': {
                const { chatRoomId: readRoomId, userId: readUserId, lastReadAt } = event.payload || {};
                if (!readRoomId || !readUserId) break;

                // Update chatRooms participant lastReadAt
                setChatRooms(prev => prev.map(room => {
                    if (room.id === readRoomId && room.participants) {
                        return {
                            ...room,
                            participants: room.participants.map(p =>
                                p.userId === readUserId ? { ...p, lastReadAt } : p
                            )
                        };
                    }
                    return room;
                }));

                // Update activeChatRoom participant lastReadAt
                setActiveChatRoom(prev => {
                    if (prev?.id === readRoomId && prev.participants) {
                        return {
                            ...prev,
                            participants: prev.participants.map(p =>
                                p.userId === readUserId ? { ...p, lastReadAt } : p
                            )
                        };
                    }
                    return prev;
                });
                break;
            }
            default:
                break;
        }
    }, [fetchChatRooms, userId]);

    const handleRoomEvent = useCallback((event) => {
        switch (event.type) {
            case 'MESSAGE_EDITED':
                setMessages(prev => prev.map(msg =>
                    msg.id === event.payload?.id ? event.payload : msg
                ));
                break;
            case 'MESSAGE_DELETED':
                setMessages(prev => prev.map(msg =>
                    msg.id === event.payload?.id ? event.payload : msg
                ));
                break;
            case 'MESSAGE_PINNED':
                setMessages(prev => prev.map(msg =>
                    msg.id === event.payload?.id ? { ...msg, pinned: true } : msg
                ));
                break;
            case 'MESSAGE_UNPINNED':
                setMessages(prev => prev.map(msg =>
                    msg.id === event.payload?.id ? { ...msg, pinned: false } : msg
                ));
                break;
            case 'REACTION_ADDED':
                setMessages(prev => prev.map(msg => {
                    if (msg.id === event.payload?.messageId) {
                        const reactions = msg.reactions ? [...msg.reactions] : [];
                        const reactionData = event.payload.reaction || event.payload;
                        // Avoid duplicates
                        if (!reactions.some(r => r.userId === reactionData.userId && r.emoji === reactionData.emoji)) {
                            reactions.push(reactionData);
                        }
                        return { ...msg, reactions };
                    }
                    return msg;
                }));
                break;
            case 'REACTION_REMOVED':
                setMessages(prev => prev.map(msg => {
                    if (msg.id === event.payload?.messageId) {
                        const reactionData = event.payload.reaction || event.payload;
                        const reactions = (msg.reactions || []).filter(
                            r => !(r.userId === reactionData.userId && r.emoji === reactionData.emoji)
                        );
                        return { ...msg, reactions };
                    }
                    return msg;
                }));
                break;
            case 'CHAT_FINISHED':
            case 'CHAT_RENEWED':
                if (activeChatRoom?.id === event.payload?.chatRoomId) {
                    fetchChatRoom(event.payload.chatRoomId);
                }
                fetchChatRooms();
                break;
            case 'PARTICIPANTS_ADDED':
            case 'PARTICIPANT_REMOVED':
                if (activeChatRoom?.id === event.payload?.chatRoomId) {
                    fetchChatRoom(event.payload.chatRoomId);
                }
                break;
            case 'PRESENCE_UPDATE': {
                const { userId: presenceUserId, online } = event.payload || {};
                if (presenceUserId) {
                    setOnlineUsers(prev => ({ ...prev, [presenceUserId]: online }));
                }
                break;
            }
            default:
                break;
        }
    }, [activeChatRoom, fetchChatRoom, fetchChatRooms]);

    // Open a chat room
    const openChatRoom = useCallback(async (chatRoomId) => {
        const room = await fetchChatRoom(chatRoomId);
        if (room) {
            await fetchMessages(chatRoomId);
            subscribeToChatRoom(chatRoomId);
            await markAsRead(chatRoomId);
            fetchPresence(chatRoomId);

            // Signal presence to other participants
            const client = stompClientRef.current;
            if (client?.connected) {
                client.publish({ destination: '/app/chat.enterRoom', body: JSON.stringify(chatRoomId) });
            }

            // Heartbeat every 30s to keep presence alive
            if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = setInterval(() => {
                const c = stompClientRef.current;
                if (c?.connected) {
                    c.publish({ destination: '/app/chat.heartbeat' });
                }
            }, 30000);
        }
    }, [fetchChatRoom, fetchMessages, subscribeToChatRoom, markAsRead, fetchPresence]);

    // Close active chat room
    const closeChatRoom = useCallback(() => {
        const chatRoomId = activeChatRoomRef.current?.id;

        if (subscriptionsRef.current.messages) {
            try { subscriptionsRef.current.messages.unsubscribe(); } catch (e) { /* ignore */ }
        }
        if (subscriptionsRef.current.events) {
            try { subscriptionsRef.current.events.unsubscribe(); } catch (e) { /* ignore */ }
        }

        // Signal leaving the room
        const client = stompClientRef.current;
        if (client?.connected && chatRoomId) {
            client.publish({ destination: '/app/chat.leaveRoom', body: JSON.stringify(chatRoomId) });
        }

        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }

        setActiveChatRoom(null);
        setMessages([]);
    }, []);

    // Initial fetch
    useEffect(() => {
        if (userId) {
            fetchChatRooms();
        }
    }, [userId, fetchChatRooms]);

    const value = {
        chatRooms,
        archivedChatRooms,
        activeChatRoom,
        messages,
        loading,
        messagesLoading,
        error,
        totalUnreadCount,
        typingUsers,
        onlineUsers,
        fetchChatRooms,
        fetchArchivedChatRooms,
        openChatRoom,
        closeChatRoom,
        sendMessage,
        editMessage,
        deleteMessage,
        markAsRead,
        createDirectChat,
        createTrainingGroupChat,
        createSchoolAnnouncementChat,
        addAllSchoolOwners,
        createCustomGroupChat,
        createTrainerChat,
        finishTrainingGroupChat,
        renewTrainingGroupChat,
        deleteChat,
        addParticipants,
        removeParticipant,
        fetchMessages,
        sendTypingEvent,
        toggleMute,
        pinMessage,
        unpinMessage,
        fetchPinnedMessages,
        addReaction,
        removeReaction,
        fetchPresence,
        requestNotificationPermission,
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};
